import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export type MatchConfidence = 'strong' | 'potential';

export type MatchStatus =
  | 'matched'
  | 'application_opened'
  | 'applying'
  | 'application_unknown'
  | 'applied'
  | 'auto_applied'
  | 'applied_failed'
  | 'worker_dismissed'
  | 'dismissed'
  | 'booked'
  | 'instant_offer'
  | 'canceled'
  | 'cancelled';

export interface MatchMetadata {
  engineVersion?: string;
  confidence?: MatchConfidence;
  missingSignals?: string[];
  uncertainties?: string[];
  profileComplete?: boolean;
  [key: string]: unknown;
}

export interface Match {
  id: number;
  scraped_job_id: number;
  worker_id: number;
  match_score: number;
  match_reasons: string[];
  match_confidence?: MatchConfidence;
  match_metadata?: MatchMetadata | string | null;
  status: MatchStatus;
  title: string;
  company: string;
  location: string | null;
  rate: string | null;
  rate_min?: number | null;
  rate_max?: number | null;
  rate_type?: string | null;
  pay_provenance?: string | null;
  source: string;
  source_url: string | null;
  apply_contact?: string | null;
  apply_method: string;
  job_type: string;
  roles_needed: string[];
  start_date: string | null;
  listing_found_at?: string | null;
  activity_status?: string | null;
  activity_checked_at?: string | null;
  currently_qualified: boolean;
  application_opened_at?: string | null;
  applied_confirmed_at?: string | null;
  dismissal_reason?: string | null;
  created_at: string;
}

export interface MatchStats {
  total_matches: number;
  pending_matches: number;
  opened_applications: number;
  applied_matches: number;
  dismissed_matches: number;
  average_match_score: number;
}

export interface MatchesState {
  matches: Match[];
  stats: MatchStats | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
}

const initialState: MatchesState = {
  matches: [],
  stats: null,
  loading: false,
  loadingMore: false,
  hasMore: false,
  error: null,
};

export const fetchMatches = createAsyncThunk(
  'matches/fetchMatches',
  async (
    filters: {
      status?: string;
      confidence?: MatchConfidence;
      min_score?: number;
      job_type?: string;
      limit?: number;
      offset?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });

      const response = await api.get(`/matches?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch matches');
      }
      return rejectWithValue('Failed to fetch matches');
    }
  }
);

export const loadMoreMatches = createAsyncThunk(
  'matches/loadMoreMatches',
  async (
    filters: {
      status?: string;
      confidence?: MatchConfidence;
      min_score?: number;
      job_type?: string;
      limit?: number;
      offset?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });

      const response = await api.get(`/matches?${params.toString()}`);
      return { matches: response.data, limit: filters.limit || 50 };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to load more matches');
      }
      return rejectWithValue('Failed to load more matches');
    }
  }
);

export const fetchMatchStats = createAsyncThunk(
  'matches/fetchMatchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/matches/stats');
      return response.data.matches;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch match stats');
      }
      return rejectWithValue('Failed to fetch match stats');
    }
  }
);

export const refreshMatches = createAsyncThunk(
  'matches/refreshMatches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/matches/refresh');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to refresh matches');
      }
      return rejectWithValue('Failed to refresh matches');
    }
  }
);

export const dismissMatch = createAsyncThunk(
  'matches/dismissMatch',
  async (
    { id, reason }: {
      id: number;
      reason: 'wrong_role' | 'too_far' | 'pay_too_low' | 'company' | 'other';
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(`/matches/${id}/dismiss`, { reason });
      return { id, match: response.data.match || response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to dismiss match');
      }
      return rejectWithValue('Failed to dismiss match');
    }
  }
);

export const openApplication = createAsyncThunk(
  'matches/openApplication',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/matches/${id}/application-opened`);
      return { id, match: response.data.match || response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Could not record the opened application');
      }
      return rejectWithValue('Could not record the opened application');
    }
  },
);

export const confirmApplied = createAsyncThunk(
  'matches/confirmApplied',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/matches/${id}/confirm-applied`);
      return { id, match: response.data.match || response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Could not confirm the application');
      }
      return rejectWithValue('Could not confirm the application');
    }
  },
);

export const undoDismiss = createAsyncThunk(
  'matches/undoDismiss',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/matches/${id}/undo-dismiss`);
      return { id, match: response.data.match || response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Could not restore this match');
      }
      return rejectWithValue('Could not restore this match');
    }
  },
);

// Compatibility action retained for older callers. New UI uses confirmApplied.
export const markApplied = createAsyncThunk(
  'matches/markApplied',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/matches/${id}/mark-applied`);
      return { id, ...response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to mark as applied');
      }
      return rejectWithValue('Failed to mark as applied');
    }
  }
);

const matchesSlice = createSlice({
  name: 'matches',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.matches = action.payload;
        state.hasMore = action.payload.length >= 50;
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loadMoreMatches.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(loadMoreMatches.fulfilled, (state, action) => {
        state.loadingMore = false;
        state.matches = [...state.matches, ...action.payload.matches];
        state.hasMore = action.payload.matches.length >= action.payload.limit;
      })
      .addCase(loadMoreMatches.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.payload as string;
      })
      .addCase(fetchMatchStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatchStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchMatchStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(refreshMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshMatches.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.matches) {
          state.matches = action.payload.matches;
        }
      })
      .addCase(refreshMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(dismissMatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(dismissMatch.fulfilled, (state, action) => {
        state.loading = false;
        state.matches = state.matches.filter((match) => match.id !== action.payload.id);
      })
      .addCase(dismissMatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(markApplied.fulfilled, (state, action) => {
        const match = state.matches.find((m) => m.id === action.payload.id);
        if (match) match.status = 'applied';
      })
      .addCase(openApplication.fulfilled, (state, action) => {
        const match = state.matches.find((item) => item.id === action.payload.id);
        if (match) {
          match.status = 'application_opened';
          match.application_opened_at = action.payload.match.application_opened_at || match.application_opened_at;
        }
      })
      .addCase(confirmApplied.fulfilled, (state, action) => {
        const match = state.matches.find((item) => item.id === action.payload.id);
        if (match) {
          match.status = 'applied';
          match.applied_confirmed_at = action.payload.match.applied_confirmed_at || match.applied_confirmed_at;
        }
      })
      .addCase(undoDismiss.fulfilled, (state, action) => {
        state.matches = state.matches.filter((match) => match.id !== action.payload.id);
      });
  },
});

export default matchesSlice.reducer;
